import { ChangeEvent, FC, useMemo, useState } from "react";
import { Fab, Skeleton, Stack, TextField, Typography } from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";
import { AddIcon } from "../../../../../assets/icons";
import CheckedIndicator from "../../../../../components/common/CheckedIndicator";
import DateIndicator from "../../../../../components/common/DateIndicator";
import ErrorMessage from "../../../../../components/common/ErrorMessage";
import LockedIndicator from "../../../../../components/common/LockedIndicator";
import { CustomColumnMenu } from "../../../../../components/common/table/TableData/CustomColumnMenu";
import { CustomDataGridFooter } from "../../../../../components/common/table/TableData/TableDataFooter";
import { useCustomTableDataStyles } from "../../../../../components/common/table/TableData/useCustomTableDataStyles";
import { ApiRoutes } from "../../../../../datasources/networking/apiRoutes";
import { UserRegistry } from "../../../../../datasources/responses/adminUsers";
import { PaginatedResponse } from "../../../../../datasources/responses/common";
import { useResources } from "../../../../../hooks/useResources";
import { useCurrentTheme } from "../../../../../theme/theme";
import CustomNoRowsOverlay from "../../../../results/ResultsTransport/TransportGreenrouter/components/CustomNoRowsOverlay";
import UserDialog from "./UserDialog";

declare module "@mui/x-data-grid" {
	interface ColumnMenuPropsOverrides {
		disableSort?: boolean;
	}
}

const UsersList: FC = () => {
	const { t } = useTranslation("feature.users");
	const { theme } = useCurrentTheme();
	const tableSx = useCustomTableDataStyles();

	// Fetch users using custom hook
	const {
		data: users,
		isLoading,
		error,
		reload,
	} = useResources<PaginatedResponse<UserRegistry>>({ route: ApiRoutes.AdminUsers }, true);

	const [paginationModel, setPaginationModel] = useState({
		page: 0,
		pageSize: 10,
	});

	const [selectedUserId, setSelectedUserId] = useState<string | undefined>();
	const [dialogIsVisible, setDialogIsVisible] = useState<boolean>(false);

	// Search query state
	const [searchQuery, setSearchQuery] = useState("");

	// Open user dialog on row click
	const handleRowClick = (id: any) => {
		setSelectedUserId(id);
		setDialogIsVisible(true);
	};

	// Close user dialog
	const handleCloseDialog = () => {
		setDialogIsVisible(false);
		setSelectedUserId(undefined);
	};

	// Update search query
	const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
		setSearchQuery(event.target.value);
	};

	const handlePaginationChange = (newPaginationModel: { page: number; pageSize: number }) => {
		setPaginationModel(newPaginationModel);
	};

	const textPaletteColor = theme.palette.primary.contrastText;

	// Filter users based on search query (username, first name, last name, email)
	const filteredUsers = useMemo(() => {
		if (!users?.items) return [];
		const q = searchQuery.toLowerCase();
		return users.items.filter(
			(user) =>
				user.username.toLowerCase().includes(q) ||
				user.firstName.toLowerCase().includes(q) ||
				user.lastName.toLowerCase().includes(q) ||
				user.email.toLowerCase().includes(q)
		);
	}, [users, searchQuery]);

	const columns: GridColDef[] = [
		{
			field: "username",
			headerName: t("username"),
			flex: 1,
			renderHeader: () => (
				<Typography fontWeight={700} sx={{ color: textPaletteColor }} variant="body2">
					{t("username")}
				</Typography>
			),
		},
		{
			field: "firstName",
			headerName: t("first name"),
			flex: 1,
			renderHeader: () => (
				<Typography fontWeight={700} sx={{ color: textPaletteColor }} variant="body2">
					{t("first name")}
				</Typography>
			),
		},
		{
			field: "lastName",
			headerName: t("last name"),
			flex: 1,
			renderHeader: () => (
				<Typography fontWeight={700} sx={{ color: textPaletteColor }} variant="body2">
					{t("last name")}
				</Typography>
			),
		},
		{
			field: "email",
			headerName: t("email"),
			flex: 1,
			renderHeader: () => (
				<Typography fontWeight={700} sx={{ color: textPaletteColor }} variant="body2">
					{t("email")}
				</Typography>
			),
		},
		{
			field: "telephone",
			headerName: t("telephone"),
			flex: 1,
			renderHeader: () => (
				<Typography fontWeight={700} sx={{ color: textPaletteColor }} variant="body2">
					{t("telephone")}
				</Typography>
			),
		},
		{
			field: "preferredLanguage",
			headerName: t("preferred language"),
			flex: 1,
			renderCell: (params) =>
				params.value ? t(`preferences/language/${params.value}`, { ns: "navigation" }) : "",
			renderHeader: () => (
				<Typography fontWeight={700} sx={{ color: textPaletteColor }} variant="body2">
					{t("preferred language")}
				</Typography>
			),
		},
		{
			field: "roleDescription",
			headerName: t("role"),
			flex: 1,
			renderHeader: () => (
				<Typography fontWeight={700} sx={{ color: textPaletteColor }} variant="body2">
					{t("role")}
				</Typography>
			),
		},
		{
			field: "status",
			headerName: t("status"),
			flex: 1,
			renderCell: (params) => t("status_" + params.value),
			renderHeader: () => (
				<Typography fontWeight={700} sx={{ color: textPaletteColor }} variant="body2">
					{t("status")}
				</Typography>
			),
		},
		{
			field: "expiredAt",
			headerName: t("expires at"),
			flex: 1,
			renderCell: (params) => (
				<Stack justifyContent={"center"} height={"100%"}>
					<DateIndicator date={params.value} />
				</Stack>
			),
			renderHeader: () => (
				<Typography fontWeight={700} sx={{ color: textPaletteColor }} variant="body2">
					{t("expires at")}
				</Typography>
			),
		},
		{
			field: "blocked",
			headerName: t("blocked"),
			flex: 0.7,
			renderCell: (params) => (
				<Stack justifyContent={"center"} height={"100%"}>
					<CheckedIndicator flag={params.value} />
				</Stack>
			),
			renderHeader: () => (
				<Typography fontWeight={700} sx={{ color: textPaletteColor }} variant="body2">
					{t("blocked")}
				</Typography>
			),
		},
		{
			field: "multiFactorAuthenticationEnabled",
			headerName: t("mfa"),
			flex: 0.7,
			renderCell: (params) => (
				<Stack justifyContent={"center"} height={"100%"}>
					<CheckedIndicator flag={params.value} />
				</Stack>
			),
			renderHeader: () => (
				<Typography fontWeight={700} sx={{ color: textPaletteColor }} variant="body2">
					{t("mfa")}
				</Typography>
			),
		},
		{
			field: "isProtected",
			headerName: t("protected"),
			flex: 0.7,
			renderCell: (params) => (
				<Stack justifyContent={"center"} height={"100%"}>
					<LockedIndicator flag={params.value} />
				</Stack>
			),
			renderHeader: () => (
				<Typography fontWeight={700} sx={{ color: textPaletteColor }} variant="body2">
					{t("protected")}
				</Typography>
			),
		},
	];

	// Display error if API call fails
	if (error) {
		return <ErrorMessage httpStatusCode={error.httpCode} actions={[{ onClick: reload }]} />;
	}

	if (isLoading) {
		return <Skeleton variant="rounded" height="280px" width="100%" />;
	}

	return (
		<Stack>
			{/* Search bar */}
			<Stack sx={{ m: 2, maxWidth: 240 }}>
				<TextField
					label={t("Search")}
					variant="outlined"
					size="small"
					fullWidth
					value={searchQuery}
					onChange={handleSearchChange}
				/>
			</Stack>

			<Stack>
				<DataGrid
					// Rows and Columns
					getRowId={(row) => row.id}
					rows={filteredUsers}
					columns={columns}
					rowHeight={56}
					// Row selection
					onRowClick={handleRowClick}
					pagination
					paginationMode="server"
					rowCount={filteredUsers.length}
					pageSizeOptions={[10, 25, 50]}
					paginationModel={paginationModel}
					onPaginationModelChange={handlePaginationChange}
					disableColumnFilter
					disableDensitySelector
					disableColumnSorting
					// Cursor pointer on hover
					style={{ cursor: "pointer" }}
					// Custom components
					slots={{
						noRowsOverlay: () => <CustomNoRowsOverlay message={t("no users for this research")} />,
						footer: CustomDataGridFooter,
						columnMenu: CustomColumnMenu,
					}}
					slotProps={{
						columnMenu: {
							disableSort: true,
						},
					}}
					// Custom styles
					sx={tableSx}
				/>
			</Stack>

			{/* User dialog */}
			{dialogIsVisible && (
				<UserDialog
					id={selectedUserId}
					isOpen={dialogIsVisible}
					onCancel={handleCloseDialog}
					onSuccess={reload}
				/>
			)}

			{/* Floating Add User button */}
			<Fab disabled={isLoading} color="primary" className="add" onClick={() => handleRowClick("")}>
				<AddIcon />
			</Fab>
		</Stack>
	);
};

export default UsersList;
